import { useCallback, useEffect, useRef, useState } from "react";

const easeOut = (t: number) => 1 - (1 - t) ** 3;

const DEFAULT_THRESHOLD = 80;
const DEFAULT_DURATION = 300;

export function useDragToClose({
  onClose,
  threshold = DEFAULT_THRESHOLD,
  duration = DEFAULT_DURATION,
  open,
  expandOnDragUp = false,
}: {
  onClose: () => void;
  threshold?: number;
  duration?: number;
  open: boolean;
  expandOnDragUp?: boolean;
}) {
  const [dragHandle, setDragHandle] = useState<HTMLDivElement | null>(null);
  const [translateY, setTranslateY] = useState(0);
  const [expandHeight, setExpandHeight] = useState(0);
  const dragging = useRef(false);
  const startY = useRef(0);
  const currentY = useRef(0);
  const animationRef = useRef<number | null>(null);
  const translateYRef = useRef(translateY);
  const expandHeightRef = useRef(expandHeight);

  useEffect(() => {
    translateYRef.current = translateY;
  }, [translateY]);

  useEffect(() => {
    expandHeightRef.current = expandHeight;
  }, [expandHeight]);

  const dragProgress = expandOnDragUp
    ? Math.min(expandHeight / threshold, 1)
    : Math.min(Math.abs(translateY) / threshold, 1);
  // No backdrop opacity when in expand mode
  const backdropOpacity = expandOnDragUp ? 0 : dragProgress * 0.3;

  const animateTo = useCallback(
    (from: number, to: number, callback?: () => void) => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);

      const start = performance.now();

      const step = (now: number) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = easeOut(progress);
        const value = from + (to - from) * eased;

        setTranslateY(value);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(step);
        } else {
          callback?.();
        }
      };

      animationRef.current = requestAnimationFrame(step);
    },
    [duration],
  );

  useEffect(() => {
    if (open) {
      animateTo(0, 0);
    } else if (translateYRef.current < window.innerHeight) {
      // Only animate off-screen if not already there (drag-close handles its own animation)
      animateTo(translateYRef.current, window.innerHeight);
    }
  }, [animateTo, open]);

  useEffect(() => {
    if (!dragHandle) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" && e.pointerType !== "touch") return;
      startY.current = e.clientY;
      currentY.current = e.clientY;
      dragging.current = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      currentY.current = e.clientY;
      const diff = e.clientY - startY.current;

      if (expandOnDragUp) {
        // Only handle dragging up (negative diff) to expand the drawer
        // Ignore dragging down entirely
        if (diff < 0) {
          setExpandHeight(Math.abs(diff));
        }
      } else {
        // Original behavior: only allow dragging down
        setTranslateY(Math.max(0, diff));
      }
    };

    // Past the threshold, close the map view and show the list.
    const settleExpandDrag = () => {
      const expanded = expandHeightRef.current > threshold;
      setExpandHeight(0);
      if (expanded) onClose();
    };

    const settleCloseDrag = () => {
      const totalDrag = currentY.current - startY.current;
      const from = translateYRef.current;
      if (totalDrag < -threshold || from > threshold) {
        animateTo(from, window.innerHeight, onClose);
        return;
      }
      animateTo(from, 0);
    };

    const onPointerUp = () => {
      if (!dragging.current) return;
      dragging.current = false;

      if (expandOnDragUp) {
        settleExpandDrag();
      } else {
        settleCloseDrag();
      }
    };

    dragHandle.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    // Fix: Always clean up event listeners, even if dragHandle changes
    return () => {
      dragHandle.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [dragHandle, threshold, onClose, animateTo, expandOnDragUp]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    setDragHandle,
    style: {
      transform: `translateY(${translateY}px)`,
      transition: "none",
    },
    backdropOpacity,
    expandHeight,
  };
}
